/* eslint-env es2020 */
const SCALE = 10000n;
const MAX_UNITS = 99999999999999999999n;
const invalid = () => new Error('A bank amount is missing, ambiguous, or exceeds supported precision.');

export const bankUnits = (value) => {
  const match = /^(-?)(\d{1,16})(?:\.(\d{1,4}))?$/.exec(String(value));
  if (!match) throw invalid();
  const units = (BigInt(match[2]) * SCALE + BigInt((match[3] || '').padEnd(4, '0'))) * (match[1] ? -1n : 1n);
  if (units > MAX_UNITS || units < -MAX_UNITS) throw invalid();
  return units;
};
export const bankDecimal = (units) => {
  if (units > MAX_UNITS || units < -MAX_UNITS) throw invalid();
  const absolute = units < 0n ? -units : units;
  return `${units < 0n ? '-' : ''}${absolute / SCALE}.${String(absolute % SCALE).padStart(4, '0')}`;
};

export const parseBankAmount = (value, { nullable = false, currency = '' } = {}) => {
  if (currency && !/^[A-Z]{3}$/.test(currency)) throw invalid();
  if (value === null || value === undefined || String(value).trim() === '') {
    if (nullable) return null;
    throw invalid();
  }
  // Excel numeric cells have already passed through a double. Large values must
  // come from text; do not claim precision that the source reader has discarded.
  if (typeof value === 'number' && (!Number.isFinite(value) || Math.abs(value) >= 1e11)) throw invalid();
  let raw = String(value).trim();
  if (currency) raw = raw.replace(new RegExp(`(?:^${currency}\\s*|\\s*${currency}$)`, 'i'), '').trim();
  if (/^\(.*\)$/.test(raw)) raw = '-' + raw.slice(1, -1).trim();
  raw = raw.replace(/^\+/, '');
  if (/\s/.test(raw)) {
    if (!/^-?\d{1,3}(?:[ \u00a0\u202f]\d{3})+(?:[.,]\d{1,4})?$/.test(raw)) throw invalid();
    raw = raw.replace(/[ \u00a0\u202f]/g, '');
  }
  if (raw.includes(',') && raw.includes('.')) {
    const commaDecimal = raw.lastIndexOf(',') > raw.lastIndexOf('.');
    const grouped = commaDecimal ? /^-?\d{1,3}(?:\.\d{3})+,\d{1,4}$/ : /^-?\d{1,3}(?:,\d{3})+\.\d{1,4}$/;
    if (!grouped.test(raw)) throw invalid();
    raw = commaDecimal ? raw.replace(/\./g, '').replace(',', '.') : raw.replace(/,/g, '');
  } else if (raw.includes(',')) {
    // A lone comma followed by three digits could mean a decimal or a grouping.
    if (!/^-?\d+,\d{1,4}$/.test(raw) || /,\d{3}$/.test(raw)) throw invalid();
    raw = raw.replace(',', '.');
  }
  return bankDecimal(bankUnits(raw));
};
